import { Inspection } from './../../core/data-sources/v3/inspection/inspection.type';
import { CratesDataSourceService } from './../../core/data-sources/crates-data-source.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { InspectionService } from '../../core/services/inspection.service';
import * as moment from 'moment';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InspectionDetailsProductModalComponent } from '../../modals/inspection-details-product-modal/inspection-details-product-modal.component';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { TaskRequestSidenavService } from '../../core/services/task-request-sidenav.service';
import { ActionService } from '../../core/services/action.service';
import { Destination } from './inspection-sidenav/inspection-sidenav.component';
import { ErrorService } from '../../core/services/error.service';
import { GetAllGoodsFilterParams, GetAllGoodsSortParams } from '../../core/data-sources/v3/inspection/inspection-data-source.v3.service';

export enum InspectionComponentStatusOptions {
  OK = 'OK',
  SURPLUS = 'SURPLUS',
  MISSING = 'MISSING',
  NONE = 'NONE'
}

export enum PalletState {
  RECEIVED = 'RECEIVED',
  AWAITING_STORAGE = 'AWAITING_STORAGE'
}

enum TaskBasedOnDestiny {
  STORAGE = 'RELOCATE_STORAGE',
  STOCK_DIVERGENT = 'RELOCATE_DIVERGENT',
  QUALITY = 'RELOCATE_QUALITY',
}

export enum InspectionPageState {
  LOADING = 'LOADING',
  LOADED = 'LOADED',
  INITIAL = 'INITIAL'
}

@Component({
  selector: 'app-inspection',
  templateUrl: './inspection.component.html',
  styleUrls: ['./inspection.component.scss']
})
export class InspectionComponent implements OnInit, OnDestroy {
  InspectionPageState = InspectionPageState;
  PalletState = PalletState;
  filteredGoods: Inspection[] = [];
  selectedGoodsTagRfids = new Set();
  dataSubscription: Subscription;
  commentsSubscription$: Subscription;
  actualHour;
  state: InspectionPageState = InspectionPageState.INITIAL;
  isLoadingModal = false;
  filterParams: GetAllGoodsFilterParams;
  sortParams: GetAllGoodsSortParams;
  totalProductsCount: number | null = null;

  constructor(
    private router: Router,
    public inspectionService: InspectionService,
    public dialog: MatDialog,
    private sidenavService: TaskRequestSidenavService,
    private actionService: ActionService,
    private errorService: ErrorService,
    private cratesDataSourceService: CratesDataSourceService
  ) {}

  ngOnInit(): void {
    const map = new Map<string, Inspection>();
    this.state = InspectionPageState.LOADING;
    this.dataSubscription = this.inspectionService.items$.subscribe(items => {
      map.clear();
      const goods = [...items];
      for (const good of goods) {
        map.set(good.id, good);
      }
      this.filteredGoods = goods;
      this.actualHour = moment().format('HH:mm');
      this.state = InspectionPageState.LOADED;
    });
    this.commentsSubscription$ = this.inspectionService.crateCommentsQty$.subscribe(comments => {
      if (!comments || !comments.length) return;
      for (const comment of comments) {
        if (map.has(comment.crateId)) {
          map.get(comment.crateId).commentsQty = comment.commentsQty;
        }
      }
    });
    this.inspectionService.init();
    this.getTotalProductsCount();
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
    this.commentsSubscription$?.unsubscribe();
  }

  async getInspectionList() {
    this.state = InspectionPageState.LOADING;
    try {
      await this.inspectionService.reload(this.filterParams, this.sortParams);
    } catch (e) {
      console.log(e);
    } finally {
      this.state = InspectionPageState.LOADED;
    }
  }

  openProductDetailsModal = async (item, page) => {
    try {
      this.isLoadingModal = true;
      const crateDetails = await this.cratesDataSourceService.getCrateById(item.id);
      this.isLoadingModal = false;
      const dialogRef: MatDialogRef<InspectionDetailsProductModalComponent> = this.dialog.open(InspectionDetailsProductModalComponent, {
        panelClass: 'details-modal',
        data: { item, page, crateDetails },
        autoFocus: false
      });
      dialogRef.afterClosed().pipe(first()).subscribe(res => {
        if (res === 'SUBMITTED') {
          this.update();
        } else if (res?.openPalletInventorySidenav) {
          const data = { type: "PALLET_INVENTORY", crate: res.crate, position: crateDetails.position };
          this.sidenavService.toggle(data);
        }
      });
    } catch (err) {
      this.isLoadingModal = false;
      console.log(err);
    }
  }

  getSelectedValue(value) {
    this.selectedGoodsTagRfids = value;
  }

  openTaskRequest(good) {

    const goods = [];
    let palletType = 'PALLET';
    const tagRfid = good.tagRfid;
    let initialDestination = Destination.STORAGE;

    if (this.selectedGoodsTagRfids.has(good.tagRfid)) {
      const selected = this.filteredGoods.filter(g => this.selectedGoodsTagRfids.has(g.tagRfid));
      for (const s of selected) {
        for (const p of s.skuGroups) {
          goods.push({
            name: p.name,
            quantity: p.quantity,
            tagRfid: s.tagRfid
          });
        }
      }
      palletType = 'PALLET';
    } else {
      for (const p of good.skuGroups) {
        goods.push({
          name: p.name,
          quantity: p.quantity,
          tagRfid: good.tagRfid
        });
      }
      initialDestination = good.destination;
      palletType = good.type;
    }

    const data = {
      data: {
        goods,
        palletType,
        initialDestination,
        tagRfid
      },
      type: 'INSPECTION'
    };


    this.sidenavService.toggle(data).then(async (res: any) => {
      const { rfidTags } = res;
      const promises = [];
      for (const rfidTag of rfidTags) {
        const params = {
          type: TaskBasedOnDestiny[res.selectedDestination],
          userIds: res.userIds,
          tagRfid: rfidTag
        };
        if (!res.isCritical && res.userIds) {
          promises.push(this.sendTask(params));
        }
      }
      try {
        await Promise.all(promises);
      } catch (error) {
        this.errorService.openErrorSnackBar(error);
      }
      await this.update();

      if (res !== true) {
        this.selectedGoodsTagRfids.clear();
      }
    });
  }

  async toStocking() {
    await this.router.navigate(['stock']);
  }

  async toIn() {
    await this.router.navigate(['in']);
  }

  async update() {
    await this.getInspectionList();
    await this.getTotalProductsCount();
    this.actualHour = moment().format('HH:mm');
  }

  async sendTask(data) {
    await this.actionService.sendTask(data);
  }

  async sendCriticalTask(data) {
    await this.actionService.sendCriticalTask(data);
  }

  async getNextPage(event: boolean) {
    if (event) {
      this.state = InspectionPageState.LOADING;
      await this.inspectionService.getNextPage().finally(() => {
        this.state = InspectionPageState.LOADED;
      });
    }
  }

  async getPreviousPage(event: boolean) {
    if (event) {
      this.state = InspectionPageState.LOADING;
      await this.inspectionService.getPreviousPage().finally(() => {
        this.state = InspectionPageState.LOADED;
      });
    }
  }

  async onPageSizeChange(event: number) {
    this.state = InspectionPageState.LOADING;
    await this.inspectionService.setPageSize(event).finally(() => {
      this.state = InspectionPageState.LOADED;
    });
  }

  async onPageChange(event: number) {
    this.state = InspectionPageState.LOADING;
    await this.inspectionService.setPage(event, this.filterParams, this.sortParams).finally(() => {
      this.state = InspectionPageState.LOADED;
    });
  }

  onClicked(event) {
    this.openProductDetailsModal(event.good, event.page);
  }

  applyFilter(params) {
    this.inspectionService.currentPage = 1;
    this.filterParams = params?.filterParams;
    this.sortParams = params?.sortParams;
    this.update();
  }

  async getTotalProductsCount() {
    this.totalProductsCount = await this.inspectionService.getTotalProductsCount(this.filterParams).then(res => res.totalQuantity);
  }

  async openSidenav(type) {
    const data = { type: type, load: true };
    this.sidenavService.toggle(data).then(async (res: any) => {
      if (res) await this.update();
    });
  }

}
